import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Box, Typography, TextField } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckIcon from '@mui/icons-material/Check';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ReactComponent as CloseIcon } from '../../../../assets/accountSetting-assets/x-circle.svg';
import { Root, classes } from './utils';
import Toast from '../../utils/Toast';
import CustomTooltip from '../../utils/CustomTooltip';
import { globalClasses } from '../../utils/globalUtils';
import { ANNOTATION_TOOLTIP } from '../../../../constants/tooltipConstant';
import { ANNOTATION_TOAST } from '../../../../constants/toastConstant';
// import { ANNOTATION } from '../../../../constants/staticContent';
import { dateFormatConverter } from './dateFormatter';

const DocumentRecord = ({
  fieldName,
  data,
  updateOpenedData,
  annotationField,
  productNo,
  changedData,
  versionId,
  isEditing,
  handleSidebarMouseOver,
  handleSidebarMouseLeave,
  productNumber,
  selectedVersion,
}) => {
  const [field] = useState(fieldName);
  const [fieldData, setFieldData] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState('');
  const [isEdited, setIsEdited] = useState(false);
  const [id, setId] = useState('');
  const [dataId, setDataId] = useState('');
  const [enterPressed, setEnterPressed] = useState(false);
  const [date, setDate] = useState(null);
  const location = useLocation();
  const mergedData =
    location?.pathname.includes('bank-statement') ||
    location?.pathname.includes('creditcardstatement')
      ? data
      : Object.keys(data[0]).reduce(
          (acc, key) => [...acc, ...data[0][key]],
          [],
        );
  const handleDatePickerChange = (newPaymentDate) => {
    if (!newPaymentDate) {
      setEditedData('');
      setDate(null);
    } else {
      const datePart = dayjs(newPaymentDate.$d); // Convert to dayjs object if necessary
      let formattedDateForPayment;
      let formattedDate;

      // Split datePart into date and time parts
      const formattedDatePart = datePart.format('YYYY-MM-DD HH:mm:ss');
      const [newDatePartString, newTimePart] = formattedDatePart.split(' ');
      const [datePartString] = fieldData.split(' ');

      // Check for different formats and delimiters based on datePartString
      if (datePartString.includes('/')) {
        const parts = newDatePartString.split('-');
        // Assuming MM/DD/YYYY format
        formattedDateForPayment = `${parts[1]}/${parts[2]}/${parts[0]}`;

        const combinedDateTime = `${formattedDateForPayment} ${newTimePart || '00:00:00'}`;

        // Conditionally format date with or without time part
        if (newTimePart === '00:00:00' || !newTimePart) {
          formattedDate = dayjs(formattedDateForPayment, 'MM/DD/YYYY').format(
            'MM/DD/YYYY',
          );
        } else {
          formattedDate = dayjs(combinedDateTime, 'MM/DD/YYYY HH:mm').format(
            'MM/DD/YYYY HH:mm',
          );
        }
      } else if (datePartString.includes('-')) {
        const parts = newDatePartString.split('-');
        // Assuming YYYY-MM-DD format
        formattedDateForPayment = `${parts[0]}-${parts[1]}-${parts[2]}`;

        const combinedDateTime = `${formattedDateForPayment} ${newTimePart || '00:00:00'}`;

        // Conditionally format date with or without time part
        if (newTimePart === '00:00:00' || !newTimePart) {
          formattedDate = dayjs(formattedDateForPayment, 'YYYY-MM-DD').format(
            'DD-MMM-YYYY',
          );
        } else {
          formattedDate = dayjs(combinedDateTime, 'YYYY-MM-DD HH:mm').format(
            'DD-MMM-YYYY HH:mm',
          );
        }
      } else {
        // Handle other cases, assuming DD-MMM-YYYY format like 20-May-18
        const parts = newDatePartString.split('-');
        formattedDateForPayment = `${parts[2]}-${datePart.format('MMM')}-${parts[0]}`;

        // No time part for this format
        formattedDate = dayjs(formattedDateForPayment, 'DD-MMM-YYYY').format(
          'DD-MMM-YYYY',
        );
      }

      setEditedData(formattedDate);
      setDate(datePart); // Set date object as needed
    }
  };

  const handleEdit = () => {
    if (field.includes('Date')) {
      if (fieldData) {
        const formattedDateForPayment = dateFormatConverter(fieldData);
        const [, timePart = '00:00:00'] = fieldData.split(' '); // Extract date part only
        const parsedDate = dayjs(`${formattedDateForPayment} ${timePart}`);
        setDate(parsedDate); // Assuming setDate is a function to set the formatted date
      }
    }
    if (versionId !== '' && isEditing === null) {
      setEditMode(false);
    } else {
      setEditMode(true);
      setEditedData(fieldData);
      setIsEdited(false);
      mergedData.forEach((d) => {
        if (
          (!d.product && (d.label === annotationField || d.label === field)) ||
          (d.product &&
            d.product === productNumber &&
            (d.label === annotationField || d.label === field))
        ) {
          setId(d.id);
        }
      });
    }
  };

  const handleClearDate = () => {
    setDate(null);
    setEditedData('');
  };
  const handleSaveClick = () => {
    setFieldData(editedData);
    let labelExists = false;
    if (
      !mergedData.some((d) => d.id === id) &&
      !mergedData.some((d) => d.short_desc === field)
    ) {
      const modifiedData = {
        label: field,
        value: editedData,
        short_desc: field,
      };
      if (modifiedData.value !== '') {
        updateOpenedData(modifiedData);
        labelExists = true;
        setIsEdited(true);
      }
    }

    mergedData.forEach((d) => {
      if (d.label === field) {
        const modifiedData = d;
        modifiedData.value = editedData;
        modifiedData.validated = true;
        modifiedData.version = selectedVersion;
        updateOpenedData(d);
        labelExists = true;
        setIsEdited(true);
      }
    });
    if (labelExists === false) {
      mergedData.forEach((d) => {
        if (d.id === id) {
          const modifiedData = d;
          modifiedData.value = editedData;
          modifiedData.validated = true;
          modifiedData.version = selectedVersion;
          updateOpenedData(d);
          setIsEdited(true);
          Toast({ message: ANNOTATION_TOAST.DATA_CHANGED, variant: 'success' });
        }
      });
    }
    setEditMode(false);
  };

  const handleCancelClick = () => {
    setEditedData(fieldData);
    setEditMode(false);
    setIsEdited(isEdited);
    setId('');
  };

  const handleChange = (e) => {
    setEditedData(e.target.value);
    setEnterPressed(false);
  };

  const handleEnterKey = (event) => {
    if (event.key === 'Enter' && !enterPressed) {
      setEnterPressed(true);
      if (dataId === id) {
        mergedData.forEach((d) => {
          if (d.id === id) {
            const modifiedData = d;
            modifiedData.value = editedData;
            modifiedData.validated = true;
            modifiedData.version = selectedVersion;
            updateOpenedData(d);
            setIsEdited(true);
            setEditMode(false);
            Toast({
              message: ANNOTATION_TOAST.DATA_CHANGED,
              variant: 'success',
            });
          } else if (field && !enterPressed) {
            if (!mergedData.some((d) => d.short_desc === field)) {
              const modifiedData = {
                label: field,
                value: editedData,
              };
              updateOpenedData(modifiedData);
              setIsEdited(true);
              setEditMode(false);
            }
          }
        });
      }
    }
  };

  const getFieldData = () => {
    let fieldInfo = '';

    if (productNo) {
      mergedData?.forEach((d) => {
        if (d.product === productNo && d.label === annotationField) {
          fieldInfo = d.value;
        }
      });
    } else {
      mergedData?.forEach((d) => {
        if (
          d.label === annotationField ||
          (annotationField === null && d.label === field)
        ) {
          fieldInfo = d.value;
        }
      });
    }
    return fieldInfo;
  };

  useEffect(() => {
    setFieldData(getFieldData());
    mergedData.forEach((d) => {
      if (
        (!d.product && d.label === annotationField) ||
        (!d.product && d.label === field) ||
        (d.product &&
          d.product === productNumber &&
          d.label === annotationField)
      ) {
        setDataId(d.id);
      }
    });
  }, [mergedData, field, changedData, annotationField]);

  const getContainerClass = (isEditMode, isFieldData) => {
    let containerClass = classes.gridContainer;

    if (isEditMode) {
      containerClass += ` ${classes.gridContainerBackgroundColor}`;
    } else if (!isEditMode && isFieldData) {
      containerClass += ` ${classes.gridContainerBackgroundHoverColor}`;
    }

    return containerClass;
  };

  const getIconComponent = (
    isEditMode,
    edited,
    changeData,
    idOfData,
    fieldsData,
    fields,
  ) => {
    let iconClassName = classes.checkedFieldIcon;

    const hasNoBoundingBox = mergedData.some(
      (item) =>
        !item.x &&
        !item.y &&
        item.value === fieldsData &&
        item.label === fields,
    );
    if (hasNoBoundingBox) {
      iconClassName += ` ${classes.noBoundingBoxColor}`;
    } else {
      iconClassName += ` ${classes.editModeCheckIcon}`;
    }

    if (isEditMode) {
      return (
        <CheckIcon
          className={`${classes.checkedFieldIcon} ${classes.editModeCheckIcon}`}
        />
      );
    }
    if (edited) {
      const valueValid = changeData.some(
        (item) => item.id === idOfData && item.value === fieldsData,
      );
      return valueValid ? (
        <CheckIcon
          className={`${classes.checkCloseIcon} ${classes.validateCheckIcon}`}
        />
      ) : (
        <CheckIcon className={iconClassName} />
      );
    }
    const isValueValid = changeData.some(
      (item) => item.id === idOfData && item.value === fieldsData,
    );
    return isValueValid ? (
      <CheckIcon
        className={`${classes.checkCloseIcon} ${classes.validateCheckIcon}`}
      />
    ) : (
      <FiberManualRecordIcon className={classes.fiberManualRecordIcon} />
    );
  };

  const getClassName = ({
    edited,
    classData,
    fields,
    fieldsData,
    changeData,
    idOfData,
    idOfVersion,
    editing,
  }) => {
    let className = classes.typographyField;

    if (edited) {
      if (
        classData.some(
          (item) =>
            item.x === 0 &&
            item.y === 0 &&
            item.value === fieldsData &&
            item.label === fields,
        )
      ) {
        className += ` ${classes.noBoundingBoxColor}`;
      } else if (
        changeData.some(
          (item) => item.id === idOfData && item.value === fieldsData,
        )
      ) {
        className += ` ${classes.validateCheckIcon}`;
      } else {
        className += ` ${classes.editModeCheckIcon}`;
      }
    } else if (
      changeData.some(
        (item) => item.id === idOfData && item.value === fieldsData,
      )
    ) {
      className += ` ${classes.validateCheckIcon}`;
    } else {
      className += ` ${classes.sidebarTypographyColor}`;
    }

    if (idOfVersion !== '' && editing === null) {
      className += ` ${classes.cursorDisableClass}`;
    } else {
      className += ` ${globalClasses.cursorPointerClass}`;
    }

    return className;
  };

  const getFieldComponent = () => {
    if (editMode && field.includes('Date')) {
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            clearable
            disabled={versionId !== '' && isEditing === null}
            required
            reduceAnimations
            format="MM/DD/YYYY HH:mm"
            slotProps={{
              field: {
                clearable: true,
                onClear: handleClearDate,
                size: 'small',
                sx: { width: '100%' },
                error: false,
              },
              textField: {
                inputProps: {
                  readOnly: true,
                },
              },
            }}
            value={date || null}
            onChange={handleDatePickerChange}
            views={['year', 'month', 'day', 'hours', 'minutes']}
          />
        </LocalizationProvider>
      );
    }
    if (editMode) {
      return (
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={3}
          className={classes.editField}
          value={editedData}
          onChange={handleChange}
          onKeyDown={handleEnterKey}
        />
      );
    }
    return (
      <CustomTooltip title={fieldData}>
        <Typography
          variant="body1"
          onClick={handleEdit}
          onChange={handleChange}
          value={fieldData}
          className={getClassName({
            edited: isEdited,
            classData: mergedData,
            fields: field,
            fieldsData: fieldData,
            changeData: changedData,
            idOfData: dataId,
            idOfVersion: versionId,
            editing: isEditing,
          })}
        >
          {fieldData}
        </Typography>
      </CustomTooltip>
    );
  };

  // const getNoBoundingBoxComponent = (edited, classData, fieldsData, fields) => {
  //   if (edited) {
  //     const hasNoBoundingBox = classData.some((item) => !item.x && !item.y
  //     && item.value === fieldsData && item.label === fields);
  //     if (hasNoBoundingBox) {
  //       return (
  //         <Typography
  //           className={classes.noBoundingBoxTypography}
  //         >
  //           {ANNOTATION.INVOICE_ANNOTATION_MESSAGE.NO_BOUNDING_BOX}
  //         </Typography>
  //       );
  //     }
  //   } else {
  //     const hasNoBoundingBox = classData.some((i) => i.label === fields
  //     && i.value === fieldsData && !i.x && !i.y);
  //     if (hasNoBoundingBox) {
  //       return (
  //         <Typography
  //           className={classes.noBoundingBoxTypography}
  //         >
  //           {ANNOTATION.INVOICE_ANNOTATION_MESSAGE.NO_BOUNDING_BOX}
  //         </Typography>
  //       );
  //     }
  //   }
  //   return null;
  // };

  return (
    <Root className={classes.root}>
      <Grid
        container
        className={getContainerClass(editMode, fieldData)}
        onMouseOver={() => {
          const selectedItem = mergedData.find((d) => d.id === dataId);
          if (selectedItem) {
            handleSidebarMouseOver(selectedItem);
          }
        }}
        onMouseLeave={handleSidebarMouseLeave}
      >
        <Grid
          item
          xs={1}
          className={globalClasses.displayFlexAlignJustifyContent}
        >
          {getIconComponent(
            editMode,
            isEdited,
            changedData,
            dataId,
            fieldData,
            field,
          )}
        </Grid>
        <Grid item xs={4} className={classes.gridAlign}>
          <CustomTooltip title={ANNOTATION_TOOLTIP.EDIT_LINK}>
            <Typography
              variant="body1"
              onClick={handleEdit}
              className={`${classes.typographyField} ${versionId !== '' && isEditing === null ? classes.cursorDefaultClass : globalClasses.cursorPointerClass}`}
            >
              {field}
            </Typography>
          </CustomTooltip>
        </Grid>

        <Grid item xs={6} className={classes.gridAlign}>
          {getFieldComponent()}
        </Grid>
        <Grid item xs={1}>
          {editMode && (
            <Box className={classes.inputGrid}>
              <CustomTooltip title={ANNOTATION_TOOLTIP.SAVE}>
                <CheckIcon
                  onClick={handleSaveClick}
                  className={classes.checkCloseIcon}
                />
              </CustomTooltip>
              <CustomTooltip title={ANNOTATION_TOOLTIP.CLOSE}>
                <CloseIcon
                  onClick={handleCancelClick}
                  className={classes.checkCloseIcon}
                />
              </CustomTooltip>
            </Box>
          )}
        </Grid>
        {/* {getNoBoundingBoxComponent(isEdited, data, fieldData, field)} */}
      </Grid>
    </Root>
  );
};
DocumentRecord.defaultProps = {
  annotationField: null,
  productNo: null,
  versionId: '',
  isEditing: null,
  productNumber: undefined,
  data: [
    {
      id: undefined,
      x: undefined,
      y: undefined,
      width: undefined,
      height: undefined,
    },
  ],
};
DocumentRecord.propTypes = {
  fieldName: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      x: PropTypes.number,
      y: PropTypes.number,
      width: PropTypes.number,
      height: PropTypes.number,
      label: PropTypes.string,
      value: PropTypes.string,
      product: PropTypes.number,
    }),
  ),
  updateOpenedData: PropTypes.func.isRequired,
  annotationField: PropTypes.string,
  productNo: PropTypes.number,
  changedData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
      label: PropTypes.string,
      value: PropTypes.string,
      product: PropTypes.number,
    }),
  ).isRequired,
  versionId: PropTypes.string,
  isEditing: PropTypes.bool,
  handleSidebarMouseOver: PropTypes.func.isRequired,
  handleSidebarMouseLeave: PropTypes.func.isRequired,
  productNumber: PropTypes.number,
  selectedVersion: PropTypes.string.isRequired,
};
export default DocumentRecord;
